import React, { useState, useCallback, useMemo } from 'react';
import dagre from 'dagre';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { mixFlavorColors, getStrainFlavors, getFlavorIcon, baseFlavorColors } from '../data/flavorProfile';
import '../styles/FlavorTree.css';

// Convert rgb color to greyscale
const toGreyscale = (colorData) => {
  const L = Math.round(0.299 * colorData.r + 0.587 * colorData.g + 0.114 * colorData.b);
  return `rgb(${L}, ${L}, ${L})`;
};

// Generate psychedelic gradient pattern for a node
// activeFlavors is a Set of selected flavor names (empty = show all)
const createPsychedelicPattern = (nodeId, flavors, activeFlavors = null) => {
  if (!flavors || flavors.length === 0) return null;
  
  // Parse flavor data - handle both strings and vote objects
  let flavorData = [];
  
  if (typeof flavors[0] === 'string') {
    // Simple string array - create equal proportions
    flavorData = flavors.slice(0, 6).map(name => ({
      name,
      votes: 1,
      proportion: 1 / Math.min(flavors.length, 6)
    }));
  } else if (flavors[0] && flavors[0].name && flavors[0].votes !== undefined) {
    // Vote data available - filter votes > 0 and calculate proportions
    const validFlavors = flavors.filter(f => f.votes > 0).slice(0, 6);
    const totalVotes = validFlavors.reduce((sum, f) => sum + f.votes, 0);
    
    if (totalVotes === 0) return null;
    
    flavorData = validFlavors.map(f => ({
      name: f.name,
      votes: f.votes,
      proportion: f.votes / totalVotes
    }));
  } else {
    return null;
  }
  
  if (flavorData.length === 0) return null;

  const hasFilter = activeFlavors && activeFlavors.size > 0;
  
  // Get colors for each flavor — greyscale non-selected when a filter is active
  const coloredFlavors = flavorData.map(f => {
    const colorData = baseFlavorColors[f.name];
    if (!colorData) return null;
    const isActive = !hasFilter || activeFlavors.has(f.name);
    const color = isActive
      ? `rgb(${colorData.r}, ${colorData.g}, ${colorData.b})`
      : toGreyscale(colorData);
    return {
      name: f.name,
      color,
      proportion: f.proportion,
      votes: f.votes,
      isActive
    };
  }).filter(Boolean);
  
  if (coloredFlavors.length === 0) return null;
  
  // Create concentric circle stops
  // Innermost = highest votes, outermost = lowest votes
  const stops = [];
  let cumulativeProportion = 0;
  
  coloredFlavors.forEach((flavor, index) => {
    const startRadius = Math.sqrt(cumulativeProportion) * 100;
    cumulativeProportion += flavor.proportion;
    const endRadius = Math.sqrt(cumulativeProportion) * 100;
    
    if (index === 0) {
      // Center circle
      stops.push({ offset: 0, color: flavor.color });
      stops.push({ offset: endRadius, color: flavor.color });
    } else {
      // Outer rings with 1% blend transition
      stops.push({ offset: startRadius, color: flavor.color });
      stops.push({ offset: endRadius, color: flavor.color });
    }
  });
  
  return {
    id: `gradient-${nodeId}`,
    stops: stops
  };
};

// Build graph structure: ONE node per unique strain (deduped by id), edges for parent→child
const buildGraphFromTree = (rootNode) => {
  const nodes = new Map(); // id → node data
  const edges = [];        // { source, target }
  const edgeSet = new Set();

  const traverse = (node, childId = null) => {
    const rawId = node.id || node.name.toLowerCase().replace(/\s+/g, '_');
    const nodeId = rawId.replace(/-/g, '_'); // dagre crashes on hyphenated IDs
    const flavors = getStrainFlavors(node);

    if (!nodes.has(nodeId)) {
      nodes.set(nodeId, {
        id: nodeId,
        name: node.name,
        type: node.type,
        flavors,
        thc: node.thc,
        cbd: node.cbd
      });
    }

    if (childId) {
      const key = `${nodeId}->${childId}`;
      if (!edgeSet.has(key)) {
        edges.push({ source: nodeId, target: childId });
        edgeSet.add(key);
      }
    }

    if (node.parents) {
      Object.values(node.parents).forEach(parent => traverse(parent, nodeId));
    }
  };

  traverse(rootNode);
  return { nodes: Array.from(nodes.values()), edges };
};

// Custom Sugiyama-style hierarchical layout:
//   1. Longest-path rank assignment (leaves=top, root=bottom)
//   2. DFS pre-order initialisation (left subtree visited first → placed left)
//   3. Alternating barycenter passes for crossing minimisation
//   4. Centre-align each rank row
const calculateLayout = (nodes, edges) => {
  if (nodes.length === 0) return [];

  const NODE_W = 220;
  const NODE_H = 110;
  const X_GAP  = 200;   // horizontal gap between nodes on the same rank
  const Y_GAP  = 280;  // vertical gap between ranks

  // ── adjacency ────────────────────────────────────────────────────────────
  // edges: source = ancestor,  target = descendant (toward the root strain)
  const parentsOf  = {}; // id → [ancestor ids]
  const childrenOf = {}; // id → [descendant ids]
  nodes.forEach(n => { parentsOf[n.id] = []; childrenOf[n.id] = []; });
  edges.forEach(e => {
    childrenOf[e.source].push(e.target);
    parentsOf[e.target].push(e.source);
  });

  // ── 1. rank assignment via Kahn topological sort ─────────────────────────
  //    rank 0 = top row (leaves), maxRank = bottom row (root)
  const inDeg = {};
  nodes.forEach(n => { inDeg[n.id] = parentsOf[n.id].length; });
  const queue = nodes.filter(n => inDeg[n.id] === 0).map(n => n.id);
  const rank  = {};
  nodes.forEach(n => { rank[n.id] = 0; });

  while (queue.length) {
    const cur = queue.shift();
    childrenOf[cur].forEach(child => {
      rank[child] = Math.max(rank[child], rank[cur] + 1);
      if (--inDeg[child] === 0) queue.push(child);
    });
  }

  // ── 2. group by rank ──────────────────────────────────────────────────────
  const maxRank = Math.max(...nodes.map(n => rank[n.id]));
  const byRank  = Array.from({ length: maxRank + 1 }, () => []);
  nodes.forEach(n => byRank[rank[n.id]].push(n));

  // ── 3. initial order: sort by parent positions ───────────────────────────
  const root = nodes.find(n => childrenOf[n.id].length === 0);
  const initialPos = {};
  
  // Start from the root and work upward
  byRank[maxRank].forEach((node, idx) => {
    initialPos[node.id] = idx * 1000; // Large spacing for root level
  });
  
  // Position ancestors based on their descendants
  for (let r = maxRank - 1; r >= 0; r--) {
    byRank[r].forEach(node => {
      const childPositions = childrenOf[node.id]
        .filter(c => rank[c] === r + 1 && initialPos[c] !== undefined)
        .map(c => initialPos[c]);
      if (childPositions.length > 0) {
        initialPos[node.id] = childPositions.reduce((a, b) => a + b, 0) / childPositions.length;
      } else {
        initialPos[node.id] = Object.keys(initialPos).length * 1000;
      }
    });
  }
  
  // Sort each rank by initial position
  byRank.forEach(g => g.sort((a, b) => initialPos[a.id] - initialPos[b.id]));

  // ── 4. center children between their parents ─────────────────────────────
  const getIdx = id => byRank[rank[id]].findIndex(n => n.id === id);
  const positions = {};
  
  // Assign initial positions
  byRank.forEach((group, r) => {
    group.forEach((node, idx) => {
      positions[node.id] = idx;
    });
  });
  
  // Refine positions with multiple passes
  for (let pass = 0; pass < 12; pass++) {
    // Position children centered under their parents
    for (let r = 1; r <= maxRank; r++) {
      byRank[r].forEach(node => {
        const parentIdxs = parentsOf[node.id]
          .filter(p => rank[p] === r - 1)
          .map(p => positions[p]);
        if (parentIdxs.length > 0) {
          const targetPos = parentIdxs.reduce((a, b) => a + b, 0) / parentIdxs.length;
          positions[node.id] = positions[node.id] * 0.7 + targetPos * 0.3;
        }
      });
    }
    
    // Position parents centered over their children
    for (let r = maxRank - 1; r >= 0; r--) {
      byRank[r].forEach(node => {
        const childIdxs = childrenOf[node.id]
          .filter(c => rank[c] === r + 1)
          .map(c => positions[c]);
        if (childIdxs.length > 0) {
          const targetPos = childIdxs.reduce((a, b) => a + b, 0) / childIdxs.length;
          positions[node.id] = positions[node.id] * 0.7 + targetPos * 0.3;
        }
      });
    }
  }
  
  // Sort by final positions
  byRank.forEach(g => g.sort((a, b) => positions[a.id] - positions[b.id]));

  // ── 5. assign pixel positions ─────────────────────────────────────────────
  const colStep = NODE_W + X_GAP;
  const rowStep = NODE_H + Y_GAP;

  return nodes.map(node => {
    const r     = rank[node.id];
    const group = byRank[r];
    const idx   = group.findIndex(n => n.id === node.id);
    const totalW = (group.length - 1) * colStep;
    return {
      ...node,
      x: idx * colStep - totalW / 2,  // centre the row
      y: r * rowStep                   // rank 0 at top, maxRank at bottom
    };
  });
};

// Draggable Color Legend
// activeFlavors: Set of selected flavor names
const ColorLegend = ({ activeFlavors, onFlavorClick, onClearAll }) => {
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [collapsed, setCollapsed] = useState(false);

  const handleMouseDown = (e) => {
    if (e.target.closest('.legend-toggle')) return;
    setIsDragging(true);
    setDragStart({ 
      x: e.clientX - position.x, 
      y: e.clientY - position.y 
    });
  };

  const handleTouchStart = (e) => {
    if (e.target.closest('.legend-toggle')) return;
    setIsDragging(true);
    setDragStart({ 
      x: e.touches[0].clientX - position.x, 
      y: e.touches[0].clientY - position.y 
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    setPosition({
      x: e.touches[0].clientX - dragStart.x,
      y: e.touches[0].clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <div 
      className={`color-legend ${collapsed ? 'collapsed' : ''}`}
      style={{ 
        left: `${position.x}px`, 
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="legend-header">
        <h3>Flavor Guide</h3>
        <button 
          className="legend-toggle" 
          onClick={() => setCollapsed(!collapsed)}
          onTouchEnd={(e) => {
            e.stopPropagation();
            setCollapsed(!collapsed);
          }}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? '▶' : '▼'}
        </button>
      </div>
      {!collapsed && (
        <div className="legend-items">
          {Object.entries(baseFlavorColors).map(([flavor, colorData]) => {
            const hasFilter = activeFlavors && activeFlavors.size > 0;
            const isActive = activeFlavors && activeFlavors.has(flavor);
            const isDimmed = hasFilter && !isActive;
            return (
              <div
                key={flavor}
                className={`legend-item${isActive ? ' legend-item--active' : ''}${isDimmed ? ' legend-item--dimmed' : ''}`}
                style={{ backgroundColor: isDimmed ? 'hsl(0,0%,60%)' : colorData.hsl }}
                onClick={(e) => { e.stopPropagation(); onFlavorClick && onFlavorClick(flavor); }}
                onTouchEnd={(e) => {
                  // preventDefault stops the synthesized click from firing after touchEnd
                  // which would double-toggle and cause the glitch
                  e.preventDefault();
                  e.stopPropagation();
                  onFlavorClick && onFlavorClick(flavor);
                }}
                title={isActive ? `Deselect "${flavor}"` : `Spotlight "${flavor}"`}
              >
                <span className="legend-icon">{getFlavorIcon(flavor)}</span>
                <span className="legend-name">{flavor}</span>
                {isActive && <span className="legend-active-dot">●</span>}
              </div>
            );
          })}
        </div>
      )}
      {activeFlavors && activeFlavors.size > 0 && (
        <div className="legend-clear" onClick={() => onClearAll && onClearAll()}>
          ✕ Clear all filters ({activeFlavors.size})
        </div>
      )}
    </div>
  );
};

const FlavorTree = ({ strainData }) => {
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [zoom, setZoom] = useState(0.7);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchDistance, setTouchDistance] = useState(0);
  const [pinchCenter, setPinchCenter] = useState({ x: 0, y: 0 });
  const [hiddenNodes, setHiddenNodes] = useState(new Set());
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [touchMoved, setTouchMoved] = useState(false);
  const [activeFlavors, setActiveFlavors] = useState(new Set());
  const [flavorPopup, setFlavorPopup] = useState(null); // nodeId | null
  const [nodeOffsets, setNodeOffsets] = useState({}); // nodeId -> {x, y} offset from calculated position
  const [draggingNode, setDraggingNode] = useState(null); // nodeId being dragged
  const nodeDragStartRef = React.useRef({ x: 0, y: 0 }); // SYNC ref - no stale closure issues
  const [nodeDragMoved, setNodeDragMoved] = useState(false); // track if drag actually moved
  const [isDraggingNodeTouch, setIsDraggingNodeTouch] = useState(false);
  const draggingNodeRef = React.useRef(null); // Immediate ref for touch handlers
  const containerRef = React.useRef(null);
  const svgRef = React.useRef(null);
  const hasCenteredRef = React.useRef(false);
  const stateRef = React.useRef({ zoom: 0.7, panX: 0, panY: 0 });

  const handleLegendFlavorClick = useCallback((flavor) => {
    setActiveFlavors(prev => {
      const next = new Set(prev);
      if (next.has(flavor)) {
        next.delete(flavor);
      } else {
        next.add(flavor);
      }
      return next;
    });
  }, []);

  const handleClearAllFlavors = useCallback(() => {
    setActiveFlavors(new Set());
  }, []);

  const handlePopupToggle = useCallback((e, nodeId) => {
    e.stopPropagation();
    e.preventDefault();
    setFlavorPopup(prev => prev === nodeId ? null : nodeId);
  }, []);

  // Build graph structure - ONE node per strain
  const graphData = useMemo(() => buildGraphFromTree(strainData), [strainData]);
  const layoutNodes = useMemo(() => calculateLayout(graphData.nodes, graphData.edges), [graphData]);
  
  // Apply custom offsets to node positions
  const positionedNodes = useMemo(() => {
    return layoutNodes.map(node => ({
      ...node,
      x: node.x + (nodeOffsets[node.id]?.x || 0),
      y: node.y + (nodeOffsets[node.id]?.y || 0)
    }));
  }, [layoutNodes, nodeOffsets]);

  // Calculate bounds
  const bounds = useMemo(() => {
    if (positionedNodes.length === 0) return { minX: 0, maxX: 800, minY: 0, maxY: 600 };
    const xs = positionedNodes.map(n => n.x);
    const ys = positionedNodes.map(n => n.y);
    return {
      minX: Math.min(...xs) - 150,
      maxX: Math.max(...xs) + 150,
      minY: Math.min(...ys) - 100,
      maxY: Math.max(...ys) + 100
    };
  }, [positionedNodes]);

  // Sync current state to ref so native event handlers avoid stale closures
  stateRef.current.zoom = zoom;
  stateRef.current.panX = panX;
  stateRef.current.panY = panY;

  // Center view on mount only — NOT on every zoom/bounds change (that caused screen-shift bugs)
  React.useEffect(() => {
    if (containerRef.current && !hasCenteredRef.current) {
      const dimensions = containerRef.current.getBoundingClientRect();
      if (dimensions.width > 0) {
        hasCenteredRef.current = true;
        const graphWidth = bounds.maxX - bounds.minX;
        const graphHeight = bounds.maxY - bounds.minY;
        setPanX((dimensions.width - graphWidth * zoom) / 2 - bounds.minX * zoom);
        setPanY((dimensions.height - graphHeight * zoom) / 2 - bounds.minY * zoom);
      }
    }
  }, [bounds, zoom]);

  // Mouse handlers
  const handleMouseDown = (e) => {
    // Don't start canvas dragging if clicking on a node rect or any interactive element
    if (e.target.closest('rect') || e.target.closest('circle') || e.target.closest('g[data-tap]')) {
      return;
    }
    setIsDragging(true);
    setDraggingNode(null); // Ensure node drag is off
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (draggingNodeRef.current || draggingNode) {
      // Dragging a node - ensure canvas drag is off
      if (isDragging) setIsDragging(false);
      
      const dx = (e.clientX - nodeDragStartRef.current.x) / zoom;
      const dy = (e.clientY - nodeDragStartRef.current.y) / zoom;
      
      // Mark as moved if movement exceeds threshold
      if (!nodeDragMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        setNodeDragMoved(true);
      }
      
      const nodeId = draggingNodeRef.current || draggingNode;
      setNodeOffsets(prev => ({
        ...prev,
        [nodeId]: {
          x: (prev[nodeId]?.x || 0) + dx,
          y: (prev[nodeId]?.y || 0) + dy
        }
      }));
      
      nodeDragStartRef.current = { x: e.clientX, y: e.clientY };
    } else if (isDragging) {
      // Dragging the canvas
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPanX(prev => prev + dx);
      setPanY(prev => prev + dy);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    draggingNodeRef.current = null;
    setDraggingNode(null);
    setNodeDragMoved(false);
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    // If already dragging a node (check ref for immediate state), do NOTHING
    if (draggingNodeRef.current || draggingNode) {
      return;
    }
    
    // If touching ANY part of a node's group (rect, text, etc), let the node handle it
    const isNodeElement = e.target.closest('[data-node-group]');
    
    if (isNodeElement) {
      // Don't start canvas drag - the node will handle its own touch events
      return;
    }
    
    // Clear all node drag state when starting canvas pan
    draggingNodeRef.current = null;
    setDraggingNode(null);
    setNodeDragMoved(false);
    nodeDragStartRef.current = { x: 0, y: 0 };
    
    if (e.touches.length === 1) {
      setIsDragging(true);
      setTouchMoved(false);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      setIsDragging(false);
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      setTouchDistance(Math.sqrt(dx * dx + dy * dy));
      setPinchCenter({
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2
      });
    }
  };

  const handleTouchMove = (e) => {
    // If dragging a node, the rect's own onTouchMove handles it - do NOTHING here
    if (draggingNodeRef.current || draggingNode) {
      return; // Exit immediately - let the rect handle its own drag
    }
    
    // Only process canvas operations (pan/zoom) when NOT dragging a node
    e.preventDefault();
    setTouchMoved(true);
    
    if (e.touches.length === 2 && touchDistance > 0) {
      // Pinch zoom
      setIsDragging(false);
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDistance = Math.sqrt(dx * dx + dy * dy);
      const zoomFactor = newDistance / touchDistance;
      
      // Calculate new zoom
      const newZoom = Math.max(0.2, Math.min(3, zoom * zoomFactor));
      
      // Adjust pan to zoom at pinch center point
      const zoomRatio = newZoom / zoom;
      setPanX(prev => pinchCenter.x - (pinchCenter.x - prev) * zoomRatio);
      setPanY(prev => pinchCenter.y - (pinchCenter.y - prev) * zoomRatio);
      setZoom(newZoom);
      
      setTouchDistance(newDistance);
    } else if (e.touches.length === 1 && isDragging) {
      // Canvas pan - only if isDragging is true and NOT dragging a node
      const dx = e.touches[0].clientX - dragStart.x;
      const dy = e.touches[0].clientY - dragStart.y;
      setPanX(prev => prev + dx);
      setPanY(prev => prev + dy);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };

  const handleTouchEnd = () => {
    // Completely reset ALL states (including ref)
    setIsDragging(false);
    setDragStart({ x: 0, y: 0 });
    setTouchDistance(0);
    draggingNodeRef.current = null;
    // Note: Don't reset draggingNode state here - let the box's onTouchEnd handle that
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const { zoom: currentZoom, panX: currentPanX, panY: currentPanY } = stateRef.current;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.2, Math.min(3, currentZoom * delta));
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const zoomRatio = newZoom / currentZoom;
      setPanX(mouseX - (mouseX - currentPanX) * zoomRatio);
      setPanY(mouseY - (mouseY - currentPanY) * zoomRatio);
    }
    setZoom(newZoom);
  };

  React.useEffect(() => {
    const svg = svgRef.current;
    const container = containerRef.current;
    if (svg) {
      svg.addEventListener('wheel', handleWheel, { passive: false });
      return () => svg.removeEventListener('wheel', handleWheel);
    }
  }, []);

  // Prevent touch navigation on container only for drags, not taps
  React.useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const preventNav = (e) => {
        // Only block touchmove (stops browser scroll/zoom during pan/pinch).
        // Never preventDefault on touchstart — that kills click synthesis on
        // HTML buttons (PDF, zoom controls, popup close, legend items).
        e.preventDefault();
      };
      container.addEventListener('touchmove', preventNav, { passive: false });
      return () => {
        container.removeEventListener('touchmove', preventNav);
      };
    }
  }, []);

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 0.2));
  const handleReset = () => {
    setZoom(0.7);
    if (containerRef.current) {
      const dimensions = containerRef.current.getBoundingClientRect();
      const graphWidth = bounds.maxX - bounds.minX;
      const graphHeight = bounds.maxY - bounds.minY;
      setPanX((dimensions.width - graphWidth * 0.7) / 2 - bounds.minX * 0.7);
      setPanY((dimensions.height - graphHeight * 0.7) / 2 - bounds.minY * 0.7);
    }
  };
  
  const handleResetPositions = () => {
    setNodeOffsets({});
  };

  const toggleNodeAndAncestors = (nodeId) => {
    setHiddenNodes(prev => {
      const next = new Set(prev);
      
      // Check if this node is currently hidden
      const isHidden = prev.has(nodeId);
      
      if (isHidden) {
        // Show this node and all its ancestors
        const toShow = new Set([nodeId]);
        
        // Find all ancestors
        const findAncestors = (id) => {
          graphData.edges.forEach(edge => {
            if (edge.target === id) {
              toShow.add(edge.source);
              findAncestors(edge.source);
            }
          });
        };
        findAncestors(nodeId);
        
        // Remove them from hidden set
        toShow.forEach(id => next.delete(id));
      } else {
        // Hide this node and all its ancestors
        const toHide = new Set([nodeId]);
        
        // Find all ancestors (parents, grandparents, etc)
        const findAncestors = (id) => {
          graphData.edges.forEach(edge => {
            if (edge.target === id) {
              toHide.add(edge.source);
              findAncestors(edge.source);
            }
          });
        };
        findAncestors(nodeId);
        
        // Add them to hidden set
        toHide.forEach(id => next.add(id));
      }
      
      return next;
    });
  };

  // Filter nodes and edges based on hidden nodes
  const visibleData = useMemo(() => {
    // Filter out hidden nodes
    const visibleNodes = positionedNodes.map(node => ({
      ...node,
      isHidden: hiddenNodes.has(node.id)
    }));
    
    // Filter edges - only show edges where both nodes are visible
    const visibleEdges = graphData.edges.filter(e => 
      !hiddenNodes.has(e.source) && !hiddenNodes.has(e.target)
    );
    
    return {
      nodes: visibleNodes,
      edges: visibleEdges
    };
  }, [positionedNodes, graphData.edges, hiddenNodes]);

  const width = 180;
  const height = 100;

  // PDF Download Functionality
  const handleDownloadPDF = useCallback(async () => {
    const container = containerRef.current;
    const svg = svgRef.current;
    
    if (!container || !svg) return;

    try {
      // Calculate full tree dimensions
      const graphWidth = bounds.maxX - bounds.minX;
      const graphHeight = bounds.maxY - bounds.minY;
      
      // Create a temporary container for rendering
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '1600px';
      tempContainer.style.background = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.display = 'flex';
      tempContainer.style.gap = '20px';
      document.body.appendChild(tempContainer);

      // LEFT COLUMN: Title and Legend
      const leftColumn = document.createElement('div');
      leftColumn.style.width = '200px';
      leftColumn.style.flexShrink = '0';
      
      // Create title
      const title = document.createElement('h1');
      title.textContent = `${strainData.name}`;
      title.style.marginBottom = '5px';
      title.style.fontSize = '28px';
      title.style.fontFamily = 'Arial, sans-serif';
      title.style.fontWeight = 'bold';
      
      const subtitle = document.createElement('div');
      subtitle.textContent = 'Flavor Lineage';
      subtitle.style.fontSize = '14px';
      subtitle.style.color = '#666';
      subtitle.style.marginBottom = '20px';
      subtitle.style.fontFamily = 'Arial, sans-serif';
      
      // Create legend
      const legendDiv = document.createElement('div');
      legendDiv.style.border = '2px solid #1a5c36';
      legendDiv.style.borderRadius = '8px';
      legendDiv.style.padding = '10px';
      legendDiv.style.backgroundColor = '#f9f9f9';
      
      const legendTitle = document.createElement('h3');
      legendTitle.textContent = 'Flavor Guide';
      legendTitle.style.marginTop = '0';
      legendTitle.style.marginBottom = '12px';
      legendTitle.style.fontSize = '14px';
      legendTitle.style.fontFamily = 'Arial, sans-serif';
      legendTitle.style.fontWeight = 'bold';
      legendTitle.style.borderBottom = '1px solid #ccc';
      legendTitle.style.paddingBottom = '6px';
      
      const legendList = document.createElement('div');
      legendList.style.display = 'flex';
      legendList.style.flexDirection = 'column';
      legendList.style.gap = '6px';
      
      Object.entries(baseFlavorColors).forEach(([flavor, colorData]) => {
        const item = document.createElement('div');
        item.style.display = 'flex';
        item.style.alignItems = 'center';
        item.style.padding = '6px 8px';
        item.style.backgroundColor = colorData.hsl;
        item.style.borderRadius = '4px';
        item.style.fontSize = '11px';
        item.style.fontFamily = 'Arial, sans-serif';
        item.style.fontWeight = '500';
        
        const icon = document.createElement('span');
        icon.textContent = getFlavorIcon(flavor);
        icon.style.marginRight = '6px';
        icon.style.fontSize = '14px';
        
        const name = document.createElement('span');
        name.textContent = flavor;
        
        item.appendChild(icon);
        item.appendChild(name);
        legendList.appendChild(item);
      });
      
      legendDiv.appendChild(legendTitle);
      legendDiv.appendChild(legendList);
      
      leftColumn.appendChild(title);
      leftColumn.appendChild(subtitle);
      leftColumn.appendChild(legendDiv);

      // RIGHT COLUMN: Tree SVG
      const rightColumn = document.createElement('div');
      rightColumn.style.flex = '1';
      
      // Clone the SVG and set it to show full tree
      const svgClone = svg.cloneNode(true);
      const svgWidth = 1320;
      const svgHeight = Math.min(graphHeight + 100, 850);
      
      svgClone.setAttribute('width', svgWidth);
      svgClone.setAttribute('height', svgHeight);
      svgClone.setAttribute('viewBox', `${bounds.minX - 50} ${bounds.minY - 50} ${graphWidth + 100} ${graphHeight + 100}`);
      
      // Remove transform from the main group to show tree at 100% zoom
      const mainGroup = svgClone.querySelector('g');
      if (mainGroup) {
        mainGroup.removeAttribute('transform');
      }
      
      rightColumn.appendChild(svgClone);
      
      tempContainer.appendChild(leftColumn);
      tempContainer.appendChild(rightColumn);

      // Capture as canvas with higher quality
      const canvas = await html2canvas(tempContainer, {
        scale: 3,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        width: 1600,
        windowWidth: 1600
      });

      // Remove temp container
      document.body.removeChild(tempContainer);

      // Create PDF in landscape mode
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate proper aspect ratio to avoid stretching
      const canvasAspect = canvas.width / canvas.height;
      const pdfAspect = pdfWidth / pdfHeight;
      
      let imgWidth, imgHeight, xOffset, yOffset;
      
      if (canvasAspect > pdfAspect) {
        // Canvas is wider - fit to width
        imgWidth = pdfWidth;
        imgHeight = pdfWidth / canvasAspect;
        xOffset = 0;
        yOffset = (pdfHeight - imgHeight) / 2;
      } else {
        // Canvas is taller - fit to height  
        imgHeight = pdfHeight;
        imgWidth = pdfHeight * canvasAspect;
        xOffset = (pdfWidth - imgWidth) / 2;
        yOffset = 0;
      }
      
      // Add canvas to PDF with proper aspect ratio
      pdf.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        xOffset,
        yOffset,
        imgWidth,
        imgHeight,
        undefined,
        'FAST'
      );

      // Download
      pdf.save(`${strainData.name.toLowerCase().replace(/\s+/g, '-')}-lineage-tree.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  }, [strainData, containerRef, svgRef, bounds]);

  if (!strainData || !strainData.name) {
    return <div style={{padding: '20px', color: 'red', background: 'white'}}>Error: Invalid strain data</div>;
  }

  return (
    <div 
      className="flavor-tree-container" 
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <ColorLegend activeFlavors={activeFlavors} onFlavorClick={handleLegendFlavorClick} onClearAll={handleClearAllFlavors} />
      
      <div className="tree-controls">
        <button onClick={handleZoomOut} title="Zoom Out">−</button>
        <span>{Math.round(zoom * 100)}%</span>
        <button onClick={handleZoomIn} title="Zoom In">+</button>
        <button onClick={handleReset} title="Reset View">⟲</button>
        <button onClick={handleResetPositions} title="Reset Node Positions">📍</button>
        <button onClick={handleDownloadPDF} title="Download PDF" className="download-pdf-btn">📄 PDF</button>
      </div>

      <div className="tree-instructions">
        <p>🖱️ Drag canvas to pan | 🔄 Scroll to zoom | 📦 Drag boxes to move | 👆 Touch: drag to pan, pinch to zoom</p>
      </div>

      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        style={{ display: 'block' }}
      >
        {/* Definitions - must be outside transform */}
        <defs>
          {/* Generate flavor gradients for each node */}
          {visibleData.nodes.map(node => {
            const pattern = createPsychedelicPattern(node.id, node.flavors, activeFlavors);
            if (!pattern) return null;
            
            // Radial gradient for concentric circles based on vote proportions
            return (
              <radialGradient 
                key={pattern.id} 
                id={pattern.id}
                cx="50%" 
                cy="50%" 
                r="50%"
              >
                {pattern.stops.map((stop, idx) => (
                  <stop 
                    key={idx}
                    offset={`${stop.offset}%`} 
                    stopColor={stop.color}
                  />
                ))}
              </radialGradient>
            );
          })}
          
          <filter id="drop-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="0" dy="2" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <marker
            id="arrowhead"
            markerWidth="16"
            markerHeight="16"
            refX="14"
            refY="8"
            orient="auto"
            markerUnits="userSpaceOnUse"
          >
            <path d="M0,0 L0,16 L16,8 z" fill="#1a5c36" opacity="0.9" />
          </marker>
        </defs>
        
        <g transform={`translate(${panX}, ${panY}) scale(${zoom})`}>
          {/* Render edges with curved paths */}
          {(() => {
            // Group edges by target (child node) to handle two-parent connections
            const edgesByTarget = {};
            visibleData.edges.forEach(edge => {
              if (!edgesByTarget[edge.target]) {
                edgesByTarget[edge.target] = [];
              }
              edgesByTarget[edge.target].push(edge);
            });
            
            const allPaths = [];
            const allBulges = [];
            
            Object.entries(edgesByTarget).forEach(([targetId, edges]) => {
              const target = visibleData.nodes.find(n => n.id === targetId);
              if (!target) return;
              
              const targetX = target.x;
              const targetY = target.y - height / 2; // Top of target node
              
              if (edges.length === 1) {
                // Single parent - simple curved line
                const source = visibleData.nodes.find(n => n.id === edges[0].source);
                if (!source) return;
                
                const x1 = source.x;
                const y1 = source.y + height / 2;
                const curve = Math.abs(targetX - x1) * 0.3;
                const path = `M ${x1} ${y1} C ${x1} ${y1 + curve}, ${targetX} ${targetY - curve}, ${targetX} ${targetY}`;
                
                allPaths.push(
                  <path
                    key={`edge-${edges[0].source}-${targetId}`}
                    d={path}
                    stroke="#1a5c36"
                    strokeWidth="4"
                    fill="none"
                    opacity="0.8"
                    markerEnd="url(#arrowhead)"
                    style={{ transition: 'opacity 0.3s ease' }}
                    strokeLinecap="round"
                  />
                );
              } else if (edges.length === 2) {
                // Two parents - create distinct lines that merge with a bulge
                const sources = edges.map(e => visibleData.nodes.find(n => n.id === e.source)).filter(Boolean);
                if (sources.length !== 2) return;
                
                // Sort sources by x position: left parent, right parent
                sources.sort((a, b) => a.x - b.x);
                
                // Convergence point - slightly above the target node
                const convergeY = targetY - 30;
                const convergeX = targetX;
                
                sources.forEach((source, idx) => {
                  const x1 = source.x;
                  const y1 = source.y + height / 2;
                  
                  // Create distinct curves - left parent curves from left, right from right
                  const lateralOffset = idx === 0 ? -20 : 20; // Left parent: -20, Right parent: +20
                  const midY = (y1 + convergeY) / 2;
                  const controlX1 = x1;
                  const controlY1 = midY;
                  const controlX2 = convergeX + lateralOffset;
                  const controlY2 = convergeY + 15;
                  
                  const path = `M ${x1} ${y1} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${convergeX} ${convergeY}`;
                  
                  allPaths.push(
                    <path
                      key={`edge-${source.id}-${targetId}`}
                      d={path}
                      stroke="#1a5c36"
                      strokeWidth="4"
                      fill="none"
                      opacity="0.8"
                      style={{ transition: 'opacity 0.3s ease' }}
                      strokeLinecap="round"
                    />
                  );
                });
                
                // Add line from convergence point to target
                allPaths.push(
                  <path
                    key={`edge-converge-${targetId}`}
                    d={`M ${convergeX} ${convergeY} L ${targetX} ${targetY}`}
                    stroke="#1a5c36"
                    strokeWidth="4"
                    fill="none"
                    opacity="0.8"
                    markerEnd="url(#arrowhead)"
                    style={{ transition: 'opacity 0.3s ease' }}
                    strokeLinecap="round"
                  />
                );
                
                // Add bulge at convergence point
                allBulges.push(
                  <circle
                    key={`bulge-${targetId}`}
                    cx={convergeX}
                    cy={convergeY}
                    r="8"
                    fill="#1a5c36"
                    stroke="#2d8659"
                    strokeWidth="2"
                    opacity="0.9"
                    style={{ transition: 'opacity 0.3s ease' }}
                  />
                );
              }
            });
            
            return [...allPaths, ...allBulges];
          })()}
          
          {/* Render nodes */}
          {visibleData.nodes.map(node => {
            const gradientId = `url(#gradient-${node.id})`;
            const isHidden = node.isHidden;
            
            return (
              <g 
                key={node.id}
                data-node-group="true"
                transform={`translate(${node.x}, ${node.y})`}
                style={{ 
                  transition: draggingNode === node.id ? 'none' : 'opacity 0.4s ease',
                  cursor: draggingNode === node.id ? 'grabbing' : 'grab'
                }}
                opacity={isHidden ? 0.15 : 1}
                onMouseDown={(e) => {
                  if (e.target.closest('[data-tap]')) return;
                  e.stopPropagation();
                  e.preventDefault();
                  setIsDragging(false);
                  draggingNodeRef.current = node.id;
                  setDraggingNode(node.id);
                  nodeDragStartRef.current = { x: e.clientX, y: e.clientY };
                  setNodeDragMoved(false);
                }}
                onTouchStart={(e) => {
                  if (e.target.closest('[data-tap]')) return;
                  e.stopPropagation();
                  e.preventDefault();
                  draggingNodeRef.current = node.id;
                  setIsDragging(false);
                  setDragStart({ x: 0, y: 0 });
                  setTouchDistance(0);
                  setDraggingNode(node.id);
                  nodeDragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                  setNodeDragMoved(false);
                  setTouchStartTime(Date.now());
                  setTouchMoved(false);
                }}
                onTouchMove={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (draggingNodeRef.current === node.id) {
                    setTouchMoved(true);
                    const dx = (e.touches[0].clientX - nodeDragStartRef.current.x) / zoom;
                    const dy = (e.touches[0].clientY - nodeDragStartRef.current.y) / zoom;
                    if (!nodeDragMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
                      setNodeDragMoved(true);
                    }
                    setNodeOffsets(prev => ({
                      ...prev,
                      [node.id]: {
                        x: (prev[node.id]?.x || 0) + dx,
                        y: (prev[node.id]?.y || 0) + dy
                      }
                    }));
                    nodeDragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                  }
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  const touchDuration = Date.now() - touchStartTime;
                  if (!touchMoved && !nodeDragMoved && touchDuration < 500) {
                    e.preventDefault();
                    toggleNodeAndAncestors(node.id);
                  }
                  draggingNodeRef.current = null;
                  setDraggingNode(null);
                  setNodeDragMoved(false);
                  nodeDragStartRef.current = { x: 0, y: 0 };
                }}
                onClick={(e) => {
                  if (!nodeDragMoved) {
                    e.stopPropagation();
                    toggleNodeAndAncestors(node.id);
                  }
                }}
              >
                {/* Main oval box with swirl gradient */}
                <rect
                  x={-width / 2}
                  y={-height / 2}
                  width={width}
                  height={height}
                  rx={18}
                  ry={18}
                  fill={gradientId}
                  stroke="#1a5c36"
                  strokeWidth={isHidden ? "2" : "4"}
                  opacity="0.95"
                  filter="url(#drop-shadow)"
                  style={{ pointerEvents: 'all' }}
                />
                
                {/* White fade at bottom for text readability */}
                <defs>
                  <linearGradient id={`fade-${node.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="white" stopOpacity="0" />
                    <stop offset="60%" stopColor="white" stopOpacity="0" />
                    <stop offset="100%" stopColor="white" stopOpacity="0.9" />
                  </linearGradient>
                </defs>
                <rect
                  x={-width / 2}
                  y={height / 2 - 25}
                  width={width}
                  height={25}
                  fill={`url(#fade-${node.id})`}
                  style={{ pointerEvents: 'none' }}
                />
                
                {/* Toggle indicator - positioned at top-right of box */}
                <circle
                  cx={width / 2 - 18}
                  cy={-height / 2 + 18}
                  r="12"
                  fill={isHidden ? "#7d5a9a" : "#1a5c36"}
                  stroke="white"
                  strokeWidth="2"
                  style={{ 
                    cursor: 'pointer',
                    transition: 'fill 0.3s ease',
                    pointerEvents: 'all'
                  }}
                />
                <text
                  x={width / 2 - 18}
                  y={-height / 2 + 23}
                  textAnchor="middle"
                  fill="white"
                  fontSize="16"
                  fontWeight="bold"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {isHidden ? '+' : '−'}
                </text>

                {/* Flavor info button — top-left */}
                <g
                  data-tap="true"
                  onClick={(e) => handlePopupToggle(e, node.id)}
                  onTouchEnd={(e) => {
                    const dur = Date.now() - touchStartTime;
                    if (!touchMoved && dur < 500) handlePopupToggle(e, node.id);
                  }}
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                >
                  <circle
                    cx={-width / 2 + 18}
                    cy={-height / 2 + 18}
                    r="13"
                    fill={flavorPopup === node.id ? '#d4a736' : 'rgba(255,255,255,0.15)'}
                    stroke={flavorPopup === node.id ? '#fff' : 'rgba(255,255,255,0.7)'}
                    strokeWidth="1.5"
                    style={{ transition: 'fill 0.2s ease' }}
                  />
                  {/* SVG + icon */}
                  <line x1={-width/2+18} y1={-height/2+12} x2={-width/2+18} y2={-height/2+24} stroke={flavorPopup===node.id?'#1a3a20':'white'} strokeWidth="2.5" strokeLinecap="round" style={{pointerEvents:'none'}} />
                  <line x1={-width/2+12} y1={-height/2+18} x2={-width/2+24} y2={-height/2+18} stroke={flavorPopup===node.id?'#1a3a20':'white'} strokeWidth="2.5" strokeLinecap="round" style={{pointerEvents:'none'}} />
                </g>
                
                <text
                  fill="white"
                  strokeWidth="4"
                  stroke="#000"
                  paintOrder="stroke"
                  fontSize="17"
                  fontWeight="900"
                  textAnchor="middle"
                  y="-25"
                  style={{ pointerEvents: 'none', letterSpacing: '0.3px' }}
                >
                  {node.name}
                </text>
                
                <g transform="translate(0, 0)">
                  {(node.flavors || []).slice(0, 3).map((flavor, idx) => {
                    // Handle both flavor objects {name, votes, proportion} and simple strings
                    const flavorName = typeof flavor === 'string' ? flavor : flavor.name;
                    return (
                      <g key={idx}>
                        {/* Oval background for flavor icon */}
                        <ellipse
                          cx={(idx - 1) * 30}
                          cy={5}
                          rx={16}
                          ry={14}
                          fill="rgba(255, 255, 255, 0.4)"
                          stroke="rgba(255, 255, 255, 0.6)"
                          strokeWidth="1"
                          style={{ pointerEvents: 'none' }}
                        />
                        <text
                          fontSize="22"
                          textAnchor="middle"
                          x={(idx - 1) * 30}
                          y="5"
                          style={{ pointerEvents: 'none', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }}
                        >
                          {getFlavorIcon(flavorName)}
                        </text>
                      </g>
                    );
                  })}
                </g>
                
                <text
                  fill="#1a5c36"
                  fontSize="12"
                  fontWeight="800"
                  textAnchor="middle"
                  y="40"
                  style={{ pointerEvents: 'none', textTransform: 'uppercase', letterSpacing: '0.8px' }}
                >
                  {node.type}
                </text>
              </g>
            );
          })}
        </g>
      </svg>

      {/* Flavor ratio popup — rendered in HTML over the SVG */}
      {flavorPopup && (() => {
        const popNode = visibleData.nodes.find(n => n.id === flavorPopup);
        if (!popNode || popNode.isHidden) return null;
        const screenX = popNode.x * zoom + panX;
        const screenY = (popNode.y - height / 2) * zoom + panY;
        const flavors = (popNode.flavors || []).filter(f =>
          typeof f === 'object' ? f.votes > 0 : true
        );
        const totalVotes = flavors.reduce((s, f) => s + (f.votes || 1), 0);
        return (
          <div
            className="flavor-popup"
            style={{
              left: `${screenX}px`,
              top: `${screenY - 12}px`,
              transform: 'translate(-50%, -100%)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flavor-popup-header">
              <span className="flavor-popup-title">{popNode.name}</span>
              <button className="flavor-popup-close" onClick={() => setFlavorPopup(null)}>✕</button>
            </div>
            <div className="flavor-popup-subtitle">Flavor Distribution</div>
            <div className="flavor-popup-bars">
              {flavors.slice(0, 6).map((f) => {
                const name = typeof f === 'string' ? f : f.name;
                const votes = typeof f === 'string' ? 1 : f.votes;
                const pct = Math.round((votes / totalVotes) * 100);
                const colorData = baseFlavorColors[name];
                const barColor = colorData
                  ? `rgb(${colorData.r},${colorData.g},${colorData.b})`
                  : '#aaa';
                return (
                  <div key={name} className="flavor-popup-row">
                    <span className="flavor-popup-icon">{getFlavorIcon(name)}</span>
                    <span className="flavor-popup-name">{name}</span>
                    <div className="flavor-popup-bar-track">
                      <div
                        className="flavor-popup-bar-fill"
                        style={{ width: `${pct}%`, background: barColor }}
                      />
                    </div>
                    <span className="flavor-popup-pct">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default FlavorTree;
